<template>
    <SbsLink
        class="sbs-tab"
        v-bind="tabData"
        @click="emitActiveTab(tabId)">
        <template #prepend>
            <!-- @slot Слот перед контентом -->
            <slot name="prepend"></slot>
        </template>

        <slot></slot>

        <template #underline>
            <!-- @slot Слот для нижнего подчеркивания -->
            <slot
                v-if="underlined && !tabsGroupStickyUnderline"
                name="underline">
                <SbsTabUnderline v-bind="tabUnderlineData" />
            </slot>
        </template>

        <template #append>
            <!-- @slot Слот после контента -->
            <slot name="append"></slot>
        </template>
    </SbsLink>
</template>

<script>
import themesMixin from '../../mixins/themes.js';
import {
    getRandomId,
    trimTrailingSlash,
} from '../../utils/helpers.js';

export default {
    name: 'SbsTab',
    mixins: [themesMixin],
    inject: {
        tabsComponent: { default: null },
    },
    emits: ['update:active', 'SbsTabs:change'],
    computed: {
        tabId() {
            return this.id || `sbs-tab-${getRandomId()}`;
        },
        isTabsGroup() {
            return Boolean(this.tabsComponent);
        },
        tabsGroupValue() {
            return this.tabsComponent?.modelValue;
        },
        tabsGroupDisabled() {
            return this.tabsComponent?.disabled;
        },
        tabsGroupColorTheme() {
            return this.tabsComponent?.colorTheme;
        },
        tabsGroupHoverColor() {
            return this.tabsComponent?.hoverColor;
        },
        tabsGroupHoverUnderline() {
            return this.tabsComponent?.hoverUnderline;
        },
        tabsGroupUnderlined() {
            return this.tabsComponent?.underlined;
        },
        tabsGroupStickyUnderline() {
            return this.tabsComponent?.stickyUnderline;
        },
        tabsGroupUnderlineTheme() {
            return this.tabsComponent?.underlineTheme;
        },
        isTabActive() {
            return this.isTabsGroup
                ? this.tabId === this.tabsGroupValue
                : this.active;
        },
        tabClasses() {
            const classes = [
                ...this.themeClasses,
                this.$attrs.class,
                {
                    active: this.isTabActive,
                },
            ];

            return classes;
        },
        tabColorTheme() {
            return this.colorTheme || this.tabsGroupColorTheme || '';
        },
        tabDisabled() {
            return this.disabled || this.tabsGroupDisabled || false;
        },
        tabHoverColor() {
            return this.hoverColor || this.tabsGroupHoverColor || '';
        },
        tabHoverUnderline() {
            return this.hoverUnderline || this.tabsGroupHoverUnderline || '';
        },
        tabUnderlined() {
            return this.underlined || this.tabsGroupUnderlined || true;
        },
        tabUnderlineTheme() {
            return this.underlineTheme || this.tabsGroupUnderlineTheme || '';
        },
        tabData() {
            return {
                class: this.tabClasses,
                colorTheme: this.tabColorTheme,
                disabled: this.tabDisabled,
                hoverColor: this.tabHoverColor,
                hoverUnderline: this.tabHoverUnderline,
                text: this.text,
                to: this.to,
                underlined: this.tabUnderlined && !this.tabsGroupStickyUnderline && this.linkUnderlined,
                underlineTheme: this.tabUnderlineTheme,
            };
        },
        tabUnderlineData() {
            const data = {};

            if (this.tabUnderlineTheme && this.isTabActive) {
                data.underlineTheme = this.tabUnderlineTheme;
            }

            if (this.tabHoverUnderline && !this.disabled) {
                data.hoverUnderline = this.tabHoverUnderline;
            }

            return data;
        },
    },
    methods: {
        emitActiveTab(value = this.tabId) {
            if (this.isTabsGroup) {
                const emitValue = {
                    element: this.$el,
                    value,
                };

                if (this.tabsComponent) {
                    this.tabsComponent.tabChangeHandler(emitValue);
                }
            } else {
                /**
                 * Событие `change` срабатывает при выборе таба.
                 */
                this.$emit('update:active', true);
            }
        },
    },
    inheritAttrs: false,
    props: {
        /**
         * Позволяет сделать таб активным
         * @model
         */
        active: {
            type: Boolean,
            default: false,
        },
        /**
         * Тема (цветовые решения) для цвета текста
         */
        colorTheme: {
            type: String,
            default: '',
        },
        /**
         * Отключает взаимодействие с компонентом
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Тема (цветовые решения) для цвета текста при наведении
         */
        hoverColor: {
            type: String,
            default: '',
        },
        /**
         * Тема (цветовые решения) для цвета нижнего подчеркивания при наведении
         */
        hoverUnderline: {
            type: String,
            default: '',
        },
        /**
         * Уникальный идентификатор
         */
        id: {
            type: String,
            default: '',
        },
        /**
         * Текст кнопки
         */
        text: {
            type: String,
            default: '',
        },
        /**
         * Роут для `<RouterLink>` для перехода на другую страницу без перезагрузки.
         * Может быть строкой, либо объектом.
         * Имеет более высокий приоритет перед `href`
         */
        to: {
            type: [Object, String],
            default: null,
        },
        /**
         * Определяет наличие нижнего подчеркивания
         */
        underlined: {
            type: Boolean,
            default: true,
        },
        /**
         * Тема (цветовые решения) для нижнего подчеркивания
         */
        underlineTheme: {
            type: String,
            default: '',
        },
        /**
         * Определяет наличие нижнего подчеркивания у ссылки
         */
        linkUnderlined: {
            type: Boolean,
            default: true,
        },
    },
    watch: {
        $route: {
            immediate: true,
            handler() {
                if (!this.to) return;

                const pathname = trimTrailingSlash(window.location.pathname);
                const href = trimTrailingSlash(this.$router.resolve(this.to)?.href);

                if (this.isTabActive) {
                    if (pathname !== href) {
                        this.emitActiveTab('');
                    }
                } else if (pathname === href) {
                    this.emitActiveTab(this.tabId);
                }
            },
        },
    },
    mounted() {
        if (this.tabId === this.tabsGroupValue) {
            this.emitActiveTab();
        }
    },
};
</script>

<style lang="scss">
.sbs-tab {
    color: $gray-deep;

    @include has-hover {
        &:not(.disabled):hover {
            color: $black;
        }
    }

    &:not(.disabled):focus,
    &:not(.disabled).active {
        color: $black;
    }
}
</style>
